import * as React from 'react'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { useMatch } from '@reach/router'
import { default as sides, orbit } from 'src/constants/sides'
import styled from 'styled-components'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { animated, useSpring } from '@react-spring/web'
import { MenuWrapper, MenuItem, MenuItemProps } from 'src/components/layout/Navigation/Menu'
import { navigate } from 'gatsby'
import upperFirst from 'lodash/upperFirst'
import { useApi } from 'src/api'
import { Editor } from 'src/api/stores/Editor'
import { getNavConfig, NavItemKey } from 'src/components/layout/Navigation/config'
import { RouteState } from 'src/constants/routes'

const projects = 'projects'
const project = 'project'

const editorState = 'editor'
const setupState = 'setup'
const previewState = 'preview'

type NavItem = {
  mainState: string[]
  state: string[]
  title: string
  items?: MenuItemProps[]
}

const StyledNavigationItem = styled.div`
  height: 64px;
  line-height: 64px;
  font-size: 12px;
  padding: 0 8px;
  box-sizing: border-box;
  color: rgb(255, 255, 255);
  font-weight: 300;
  /* transition: background-color 200ms ease-out; */
  cursor: default;

  &:hover {
    background-color: var(--hover);
  }

  &[data-active='true'] {
    &,
    &:hover {
      background-color: rgb(52, 107, 110);
    }
  }
`

const NavigationWrapper = styled.nav`
  --hover: #3f8487;
  padding: 0 8px;
  box-sizing: border-box;
  display: flex;
  line-height: 64px;
`

type NavigationItemProps = {
  active: boolean
  title: string
  onClick: () => void
}

const NavigationItem: React.FC<NavigationItemProps> = ({ title, active, ...props }) => {
  return (
    <StyledNavigationItem data-active={active} {...props}>
      {title}
    </StyledNavigationItem>
  )
}

const routeMatch = {
  projects: '/projects/*',
  project: '/:mainState/:projectID/:state/*'
}

type ProjectNavigationProps = {
  state: string
  projectID: string
  route: typeof project | typeof projects
  routeState: RouteState
}

const ProjectNavigation: React.FC<ProjectNavigationProps> = observer(
  ({ route, state, projectID, routeState }) => {
    const match = useMatch(routeMatch[route])
    const view = useApi((root) => root.getStore({ state, projectID }))
    const items = getNavConfig(routeState)
    const [{ left }, setLeft] = useSpring(() => ({ left: 0 }))
    const [active, setMenu] = React.useState<NavItemKey | null>(null)
    const menuHandler = (item: NavItemKey) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      const { left } = e.currentTarget.getBoundingClientRect()
      setLeft({ left, immediate: true })
      setMenu(item)
    }
    const setupMouse = (item: NavItemKey) => ({
      onClick: (e: React.MouseEvent<HTMLElement>) =>
        !active ? menuHandler(item)(e) : setMenu(null),
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => active && menuHandler(item)(e)
    })
    return (
      match && (
        <NavigationWrapper>
          {Object.entries(items).map(([item, { mainState, state, ...props }], key) => {
            return (
              <NavigationItem
                {...{ key, active: active === item, ...setupMouse(item), ...props }}
              />
            )
          })}
          {active !== null && (
            <ClickAwayListener onClickAway={() => setMenu(null)}>
              <MenuWrapper {...{ style: { left } }}>
                {items[active].items?.map((props, key) => (
                  <MenuItem {...{ ...props, key }} />
                ))}
              </MenuWrapper>
            </ClickAwayListener>
          )}
        </NavigationWrapper>
      )
    )
  }
)

const MainNavigation: React.FC = ({ state, projectID, routeState }) => {
  const match = useMatch('/:main/*')
  switch (match?.main) {
    case 'project':
    case 'projects':
      return <ProjectNavigation {...{ routeState, route: match.main, state, projectID }} />
    default:
      return null
  }
}

// const ObservedMainNavigation = observer(MainNavigation)

export { MainNavigation }
